import { useCallback } from 'react'
import { CheckoutAppSelector } from '../store'
import { setExpandedComponent } from './reducer'
import { ExpandableComponent, LimitedViewElementId } from '../../model'
import { useAppDispatch, useAppSelector } from '../hooks'

const State = {
  EXPANDED: 'Expanded',
  COLLAPSED: 'Collapsed',
} as const
type StateType = typeof State
type StateKeys = keyof StateType
export type ExpandableState = StateType[StateKeys]

interface useExpandedComponentControlProps {
  expandedComponentId: ExpandableComponent | null
  setExpandedState: (state: ExpandableState) => ExpandableState | void
  toggleExpandedState: () => ExpandableState | void
  expandableComponentState: ExpandableState
}

export const useExpandedComponentId: CheckoutAppSelector<ExpandableComponent | null> = (state) => state.uiControl.expandedComponentId

export const useExpandedComponentControl = (componentId: ExpandableComponent): useExpandedComponentControlProps => {
  const dispatch = useAppDispatch()
  const expandedComponentId = useAppSelector(useExpandedComponentId)

  const expandableComponentState = expandedComponentId === componentId ? 'Expanded' : 'Collapsed'

  const setExpandedState = useCallback(
    (newState: ExpandableState): ExpandableState | void => {
      if (newState === 'Expanded') {
        dispatch(setExpandedComponent(componentId))
        return 'Expanded'
      } else {
        dispatch(setExpandedComponent(null))
        return 'Collapsed'
      }
    },
    [dispatch, componentId]
  )

  const toggleExpandedState = useCallback(() => {
    const newState = expandableComponentState === 'Expanded' ? 'Collapsed' : 'Expanded'
    dispatch(setExpandedComponent(newState === 'Expanded' ? componentId : null))
    return newState
  }, [dispatch, componentId, expandableComponentState])

  return {
    expandedComponentId,
    setExpandedState,
    toggleExpandedState,
    expandableComponentState,
  }
}

export const useLimitedViewElementControl = (elementId: LimitedViewElementId): { show: boolean } => {
  const element = useAppSelector((state) => state.uiControl.limitedViewElementControl[elementId])

  return { show: typeof element !== 'undefined' && element.view < element.maxView }
}
